/**
 * NavigationHistoryTracker — mount-once helper that stashes the *current*
 * SPA location into sessionStorage on every route change.
 *
 * Pages that render a "Back to …" link read the stash via `useBackHref`
 * during their own render pass, so by the time a destination page mounts
 * the value already present in sessionStorage is the previous page the
 * visitor was on (this tracker's effect for the current page hasn't
 * fired yet — React runs child effects before parent effects, but a
 * page's *render* still runs before every effect in the tree).
 *
 * The tracker deliberately skips "back-target" routes (pages arrived at
 * *via* a back link — e.g. `/property/`, `/compare`) so we never stash
 * one of those and then bounce the visitor forward again.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { BACK_STASH_KEY } from "@/hooks/useBackHref";

// Routes that themselves render a "Back to …" link. Never stash these
// as a back-target or we'd create a bounce-loop.
const SKIP_PREFIXES = [
  "/property/",
  "/compare",
  "/my-favourites",
  "/my-alerts",
];

function isSkippable(pathname) {
  return SKIP_PREFIXES.some((p) => pathname.startsWith(p));
}

export default function NavigationHistoryTracker() {
  const location = useLocation();

  useEffect(() => {
    if (isSkippable(location.pathname)) return;
    try {
      window.sessionStorage.setItem(
        BACK_STASH_KEY,
        `${location.pathname}${location.search}`,
      );
    } catch { /* ignore quota */ }
  }, [location.pathname, location.search]);

  return null;
}
